import { lazy, Suspense } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { HeroSection } from "@/components/landing/HeroSection";
import { HeroToContentWarp } from "@/components/landing/HeroToContentWarp";
import { HeroGlobeStage } from "@/components/landing/HeroGlobeStage";
import { TryItNowSection } from "@/components/landing/TryItNowSection";
import { RoleSectionsProvider } from "@/components/landing/RoleSectionsProvider";
import { SectionDivider } from "@/components/landing/SectionDivider";
import { MarketingHead } from "@/components/marketing/MarketingHead";

// Below-the-fold sections are lazy-loaded so the landing page can paint and
// respond to scroll immediately on mobile. Each section streams in as the
// user scrolls down. Skeleton fallbacks reserve vertical space so layout
// doesn't jump as chunks resolve.
const ComparisonSection = lazy(() =>
  import("@/components/landing/ComparisonSection").then(m => ({ default: m.ComparisonSection }))
);
const TrustStripSection = lazy(() =>
  import("@/components/landing/TrustStripSection").then(m => ({ default: m.TrustStripSection }))
);
const FAQSection = lazy(() =>
  import("@/components/landing/FAQSection").then(m => ({ default: m.FAQSection }))
);
const Founding10Section = lazy(() =>
  import("@/components/landing/Founding10Section").then(m => ({ default: m.Founding10Section }))
);
const PromoVideoSection = lazy(() =>
  import("@/components/landing/PromoVideoSection").then(m => ({ default: m.PromoVideoSection }))
);
const CABBrokerIdSection = lazy(() =>
  import("@/components/landing/CABBrokerIdSection").then(m => ({ default: m.CABBrokerIdSection }))
);
const DoubleBrokerDefenseSection = lazy(() =>
  import("@/components/landing/DoubleBrokerDefenseSection").then(m => ({ default: m.DoubleBrokerDefenseSection }))
);
const SocialProofBar = lazy(() =>
  import("@/components/landing/SocialProofBar").then(m => ({ default: m.SocialProofBar }))
);

const SectionFallback = ({ height = "min-h-[400px]" }: { height?: string }) => (
  <div className={`${height} w-full`} aria-hidden="true" />
);

const Index = () => {
  usePageMeta({
    title: "Carrier & Broker Verification 2026 · Canada-First Trust Platform",
    description: "Verify any Canadian or US carrier and broker. Continuous monitoring alerts when authority, bond, or insurance changes. $99/mo for carriers and dispatchers. $250/mo for brokers.",
    path: "/",
    image: "https://cipherandrow.com/og/home.jpg",
  });

  return (
    <div className="min-h-svh bg-background">
      <MarketingHead />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md">
        Skip to content
      </a>
      <header role="banner">
        <LandingNav />
      </header>
      <RoleSectionsProvider>
        <main id="main-content" className="relative z-10">
          <div id="hero-stage" className="relative">
            <HeroGlobeStage stageId="hero-stage" />
            <HeroSection />
            <HeroToContentWarp />
          </div>
          <TryItNowSection />
          <div className="landing-cv">
            <Suspense fallback={<SectionFallback height="min-h-[600px]" />}>
              <PromoVideoSection />
            </Suspense>
          </div>
          <div className="landing-cv">
            <Suspense fallback={<SectionFallback height="min-h-[500px]" />}>
              <ComparisonSection />
            </Suspense>
          </div>
          <div className="landing-cv">
            <Suspense fallback={<SectionFallback height="min-h-[600px]" />}>
              <DoubleBrokerDefenseSection />
            </Suspense>
          </div>
          <SectionDivider />
          <div className="landing-cv">
            <Suspense fallback={<SectionFallback height="min-h-[600px]" />}>
              <CABBrokerIdSection />
            </Suspense>
          </div>
          <div className="landing-cv">
            <Suspense fallback={<SectionFallback height="min-h-[200px]" />}>
              <TrustStripSection />
            </Suspense>
          </div>
          <div className="landing-cv">
            <Suspense fallback={<SectionFallback height="min-h-[320px]" />}>
              <SocialProofBar />
            </Suspense>
          </div>
          <div className="landing-cv">
            <Suspense fallback={<SectionFallback height="min-h-[400px]" />}>
              <FAQSection />
            </Suspense>
          </div>
          <SectionDivider />
          <div className="landing-cv">
            <Suspense fallback={<SectionFallback height="min-h-[300px]" />}>
              <Founding10Section />
            </Suspense>
          </div>
        </main>
      </RoleSectionsProvider>
      <LandingFooter />
    </div>
  );
};

export default Index;
